import React from 'react';
import { motion } from 'framer-motion';
import { FiX, FiCpu } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

// 📂 IMPORTAÇÃO DOS FILHOS
import EntityDetailsCard from './cards/EntityDetailsCard';
import AdminDetailsCard from './cards/AdminDetailsCard';
import AnnexVaultCard from './cards/AnnexVaultCard';
import { SectionTitle, InfoField } from './shared/ModalHelpers';
import { FiActivity, FiUsers } from 'react-icons/fi';

const SupremeOrderModal = ({ order, onClose }) => {
    if (!order) return null;

    const totalLines = order.itens?.reduce((acc, it) => acc + (Number(it.quantidade) || 0), 0) || 0;
    const totalMonthly = order.itens?.reduce((acc, it) => acc + ((Number(it.quantidade) || 0) * (Number(it.precoUnitario) || 0)), 0) || 0;

    /**
     * 🕵️ PROTOCOLO DE EXTRAÇÃO VIA DIPLOMATA LOCAL
     */
    const handleSecureDownload = async (trackingID, tipoDocumento) => {
        const tId = toast.loading(`Extraindo ${tipoDocumento}...`);
        try {
            const token = localStorage.getItem('token'); 
            const response = await fetch('/api/vault/extract', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ 
                    trackingID, tipoDocumento, protocolo: order.protocolo 
                })
            });

            if (!response.ok) throw new Error("Acesso negado pelo Diplomata.");

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            const extension = trackingID.includes('.') ? trackingID.split('.').pop() : 'png';
            a.download = `${tipoDocumento}_PROT_${order.protocolo}.${extension}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success("Extração Master concluída!", { id: tId });
        } catch (err) {
            toast.error(`Falha: ${err.message}`, { id: tId });
        }
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
        >
            <motion.div 
                initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                className="w-full max-w-7xl max-h-[95vh] bg-[var(--bg-card)] border-2 border-[var(--border-color)] rounded-[3rem] overflow-hidden flex flex-col shadow-2xl"
            >
                <header className="p-8 border-b border-[var(--border-color)] flex justify-between items-center bg-[var(--text-primary)]/[0.03]">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-[var(--accent-color)] rounded-2xl flex items-center justify-center text-white shadow-lg">
                            <FiCpu size={32} className="animate-pulse" />
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-[var(--accent-color)] text-white text-[9px] font-black rounded uppercase tracking-widest">Dossiê Master PJ</span>
                                <span className="text-[var(--text-secondary)] text-[10px] font-bold opacity-40 uppercase">UID: {order._id}</span>
                            </div>
                            <h2 className="text-4xl font-black italic tracking-tighter text-[var(--text-primary)] uppercase leading-none mt-1">Dossiê {order.numeroOrdem}</h2>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-4 hover:bg-red-500 hover:text-white text-[var(--text-primary)] rounded-2xl transition-all border border-[var(--border-color)] group">
                        <FiX size={24} className="group-hover:rotate-90 transition-transform" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-10 space-y-12 custom-scrollbar bg-[var(--bg-main)]">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <EntityDetailsCard order={order} />
                        <AdminDetailsCard admin={order.empresa?.administrador} />
                        
                        {/* RADAR INTERNO (IN-PLACE) */}
                        <div className="space-y-6 bg-[var(--bg-card)] p-8 rounded-[2.5rem] border border-[var(--border-color)] shadow-sm">
                            <SectionTitle icon={FiActivity} title="SITUAÇÃO DE RADAR" />
                            <div className="p-5 bg-[var(--accent-color)]/10 border border-[var(--accent-color)]/20 rounded-2xl">
                                <InfoField label="Fase Operacional" value={order.status} color="text-[var(--accent-color)]" bold font="text-2xl" />
                            </div>
                            <div className="flex justify-between items-end">
                                <InfoField label="Linhas" value={`${totalLines} Terminais`} bold font="text-xl" />
                                <div className="text-right">
                                    <span className="text-[8px] font-black uppercase opacity-30 block">Receita Mensal</span>
                                    <span className="text-2xl font-black text-emerald-500">{totalMonthly.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <AnnexVaultCard anexos={order.anexos} handleDownload={handleSecureDownload} />

                    {/* QUADRO DE SÓCIOS (IN-PLACE) */}
                    <div className="bg-[var(--bg-card)] p-8 rounded-[2.5rem] border border-[var(--border-color)] shadow-sm">
                        <SectionTitle icon={FiUsers} title="QUADRO DE SÓCIOS" />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                            {order.empresa?.socios?.map((s, idx) => (
                                <div key={idx} className="p-6 bg-[var(--bg-main)] border border-[var(--border-color)] rounded-3xl shadow-sm hover:border-[var(--accent-color)] transition-colors">
                                    <p className="text-base font-black text-[var(--text-primary)] uppercase mb-1">{s.nome}</p>
                                    <p className="text-[10px] font-black text-[var(--accent-color)] uppercase tracking-widest mb-4">{s.cargo || 'Sócio'}</p>
                                    <div className="pt-4 border-t border-[var(--border-color)] border-dashed space-y-2">
                                        <div className="flex justify-between items-center"><span className="text-[7px] font-black uppercase opacity-30 tracking-widest">CPF</span><span className="text-[10px] font-bold text-[var(--text-primary)] font-mono">{s.cpf}</span></div>
                                        <div className="flex justify-between items-center"><span className="text-[7px] font-black uppercase opacity-30 tracking-widest">NASCIMENTO</span><span className="text-[10px] font-bold text-[var(--text-primary)]">{s.dataNascimento || '---'}</span></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <footer className="p-6 border-t border-[var(--border-color)] bg-[var(--bg-card)] flex justify-between items-center text-[9px] font-black uppercase opacity-20 tracking-[0.6em]">
                    <span>MOS DIPLOMACY • COMPONENT_SYSTEM_V2.0</span>
                    <span>INTERNAL_STABLE_LINK_OK</span>
                </footer>
            </motion.div>
        </motion.div>
    );
};

export default SupremeOrderModal;